/**
 * WP-V2-TW — definicje hotspotów miasteczka (PLAN-V2 §4.2 + §6 Town).
 * Świat 6400×720: Osiedle (zachód) · Rynek Łapki (centrum) · Pole+Stajnia+Basen (wschód).
 * Czyste dane: audyt, minimapa, E2E.
 * WAVE3-G15: ttsKey + attachBuildingHotspotNarration (lektor na budynkach).
 */

import { narrator } from '../../core/Narrator.js';
import { catalogText } from '../../core/ttsCatalog.js';
import type { TownMicroInteractionKind } from './microInteractions.js';
import { TOWN_WALK_BAND } from './townLayout.js';

/**
 * Rozmiar świata Town (viewport 1280×720).
 * WAVE7: kompaktniejsze miasto — mniej chodzenia (Toca-feel).
 */
export const TOWN_WORLD_W = 3200;
export const TOWN_WORLD_H = 720;

/** Środek dzielnicy (ekran 1280 px). */
export type TownDistrictId = 'osiedle' | 'rynek' | 'pole';

export type TownDistrict = {
  id: TownDistrictId;
  labelPl: string;
  /** Lewa krawędź pasma. */
  x0: number;
  /** Prawa krawędź pasma. */
  x1: number;
  /** Środek X (landmark / minimapa). */
  cx: number;
  color: number;
};

/**
 * 3 dzielnice na świecie 3200 px (WAVE7 kompakt):
 * - Osiedle (0–1067) · Rynek (1067–2133) · Pole (2133–3200)
 */
export const TOWN_DISTRICTS: readonly TownDistrict[] = [
  {
    id: 'osiedle',
    labelPl: 'Osiedle',
    x0: 0,
    x1: 1067,
    cx: 533,
    color: 0xfff3e9,
  },
  {
    id: 'rynek',
    labelPl: 'Rynek Łapki',
    x0: 1067,
    x1: 2133,
    cx: 1600,
    color: 0xe8f5e9,
  },
  {
    id: 'pole',
    labelPl: 'Pole · Stajnia · Basen',
    x0: 2133,
    x1: 3200,
    cx: 2666,
    color: 0xe3f2fd,
  },
] as const;

export type TownHotspot = {
  /** Klucz sceny docelowej (SceneRouter.go). */
  sceneKey: string;
  /** Etykieta PL z diakrytykami (używana w minimapie / dymkach — nie renderowana jako napis na budynku). */
  labelPl: string;
  /**
   * Klucz TTS z katalogu lektora (tools/tts + ttsCatalog), np. place_house.
   * Brak → narrate z labelPl (sayText + dopasowanie aliasów).
   */
  ttsKey?: string;
  /** Ikona / emoji (zero-reading dla dzieci). */
  icon: string;
  x: number;
  y: number;
  fill: number;
  width?: number;
  height?: number;
  district: TownDistrictId;
  /**
   * Budynek bez własnej sceny — tap = lekka interakcja w miejscu (piesek podchodzi,
   * animacja + FX + dymek), zamiast SceneRouter.go(sceneKey).
   */
  interactionOnly?: boolean;
  /** Odrębny plan lekkiej zabawy bez pustej sceny. */
  microInteraction?: TownMicroInteractionKind;
};

/** Minimalny kształt Phaser GO z eventami pointer (bez twardej zależności typów Phaser w testach). */
export type HotspotNarrationTarget = {
  on: (event: string, fn: (...args: unknown[]) => void) => unknown;
};

/** Odczytaj nazwę budynku (hover = debounce, tap = immediate). */
export function speakTownHotspot(
  h: Pick<TownHotspot, 'labelPl' | 'ttsKey'>,
  opts: { immediate?: boolean } = {},
): void {
  if (h.ttsKey && catalogText(h.ttsKey)) {
    narrator.say(h.ttsKey, opts);
    return;
  }
  narrator.sayText(h.labelPl, opts);
}

/**
 * Podłącz lektor do hitboxu budynku (pointerover + pointerdown).
 * Wywołać raz po setInteractive — TownScene.spawnBuildingHotspots.
 */
export function attachBuildingHotspotNarration(
  target: HotspotNarrationTarget,
  h: Pick<TownHotspot, 'labelPl' | 'ttsKey'>,
): void {
  target.on('pointerover', () => {
    speakTownHotspot(h);
  });
  target.on('pointerdown', () => {
    speakTownHotspot(h, { immediate: true });
  });
}

/**
 * Wejścia do budynków — rozłożone po 3 dzielnicach.
 * Hit-area ≥64 px (MIN_TOUCH).
 */
/**
 * Wejścia do budynków — stopy na linii ziemi (W9-A: y ≥ TOWN_GROUND_Y),
 * hitboxy bez nachodzeń (overlappingHotspots === []).
 */
export const TOWN_BUILDING_HOTSPOTS: readonly TownHotspot[] = [
  // —— Osiedle (0–1067) ——
  {
    sceneKey: 'House',
    labelPl: 'Domek',
    ttsKey: 'place_house',
    icon: '🏠',
    x: 320,
    y: 470,
    fill: 0xffcc99,
    width: 140,
    height: 72,
    district: 'osiedle',
  },
  {
    sceneKey: 'Wardrobe',
    labelPl: 'Salon',
    ttsKey: 'place_salon',
    icon: '🎀',
    x: 620,
    y: 455,
    fill: 0xffb6c1,
    width: 140,
    height: 72,
    district: 'osiedle',
  },
  {
    sceneKey: 'Icecream',
    labelPl: 'Lodziarnia',
    ttsKey: 'place_icecream',
    icon: '🍦',
    x: 900,
    y: 490,
    fill: 0xffe4ec,
    width: 140,
    height: 72,
    district: 'osiedle',
  },
  {
    sceneKey: 'Playground',
    labelPl: 'Plac zabaw',
    ttsKey: 'place_park',
    icon: '🛝',
    x: 480,
    y: 560,
    fill: 0xa5d6a7,
    width: 130,
    height: 64,
    district: 'osiedle',
    interactionOnly: true,
    microInteraction: 'playground',
  },
  // —— Rynek Łapki (1067–2133) ——
  {
    sceneKey: 'Music',
    labelPl: 'Scena muzyczna',
    ttsKey: 'place_park',
    icon: '🎵',
    x: 1120,
    y: 540,
    fill: 0xc4a1ff,
    width: 130,
    height: 64,
    district: 'rynek',
    interactionOnly: true,
    microInteraction: 'music',
  },
  {
    sceneKey: 'Shop',
    labelPl: 'Sklep',
    ttsKey: 'place_shop',
    icon: '🛍️',
    x: 1320,
    y: 460,
    fill: 0xffe066,
    width: 140,
    height: 72,
    district: 'rynek',
  },
  {
    sceneKey: 'Cafe',
    labelPl: 'Kawiarnia',
    ttsKey: 'place_cafe',
    icon: '☕',
    x: 1540,
    y: 500,
    fill: 0xd4a574,
    width: 140,
    height: 72,
    district: 'rynek',
  },
  {
    sceneKey: 'School',
    labelPl: 'Szkoła',
    ttsKey: 'place_school',
    icon: '📚',
    x: 1780,
    y: 455,
    fill: 0xbde0fe,
    width: 150,
    height: 72,
    district: 'rynek',
  },
  {
    sceneKey: 'Park',
    labelPl: 'Park',
    ttsKey: 'place_park',
    icon: '🌳',
    x: 1660,
    y: 575,
    fill: 0x90ee90,
    width: 150,
    height: 72,
    district: 'rynek',
  },
  {
    sceneKey: 'Library',
    labelPl: 'Biblioteczka',
    ttsKey: 'place_school',
    icon: '📖',
    x: 1980,
    y: 520,
    fill: 0xffe0b2,
    width: 130,
    height: 64,
    district: 'rynek',
    interactionOnly: true,
    microInteraction: 'library',
  },
  // —— Pole (2133–3200) ——
  {
    sceneKey: 'Pool',
    labelPl: 'Basen',
    ttsKey: 'place_pool',
    icon: '🏊',
    x: 2320,
    y: 480,
    fill: 0x7ec8e3,
    width: 140,
    height: 72,
    district: 'pole',
  },
  {
    sceneKey: 'Stable',
    labelPl: 'Stajnia',
    ttsKey: 'place_stable',
    icon: '🐴',
    x: 2560,
    y: 520,
    fill: 0xc4a484,
    width: 140,
    height: 72,
    district: 'pole',
  },
  {
    sceneKey: 'Vet',
    labelPl: 'Weterynarz',
    ttsKey: 'place_vet',
    icon: '🩺',
    x: 2780,
    y: 460,
    fill: 0xcfeaf2,
    width: 140,
    height: 72,
    district: 'pole',
  },
  {
    sceneKey: 'Garden',
    labelPl: 'Ogród',
    ttsKey: 'place_garden',
    icon: '🌸',
    x: 3000,
    y: 500,
    fill: 0xe8a0bf,
    width: 150,
    height: 72,
    district: 'pole',
  },
] as const;

/** Brama Ogrodu Marzeń — wschodni kraniec (wizual + edge). */
export const TOWN_GARDEN_GATE = { x: 3100, y: 360 } as const;

/** Wizualne osiedle (WP15 API domków). */
export type EstatePlot = {
  id: string;
  labelPl: string;
  x: number;
  y: number;
  /** Czy to domek gracza (wejście do House). */
  isOwn: boolean;
  badgePl: string;
};

export const TOWN_ESTATE_PLOTS: readonly EstatePlot[] = [
  { id: 'own', labelPl: 'Twój', x: 280, y: 520, isOwn: true, badgePl: '✓' },
  { id: 'n1', labelPl: 'Domek', x: 420, y: 540, isOwn: false, badgePl: '🔒' },
  { id: 'n2', labelPl: 'Domek', x: 560, y: 520, isOwn: false, badgePl: '🔒' },
] as const;

/** Bounds ruchu gracza / NPC na mapie kompaktowej (pas chodzenia W9-A). */
export const TOWN_BOUNDS = {
  minX: 60,
  minY: TOWN_WALK_BAND.minY,
  maxX: TOWN_WORLD_W - 60,
  maxY: TOWN_WORLD_H - 60,
} as const;

/**
 * Start gracza na Rynku Łapki (centrum), w pasie chodzenia.
 * y:600 — ulica przed fasadami (collidery budynków wyżej), poza fontanną.
 */
export const TOWN_PLAYER_START = { x: 1600, y: 600 } as const;

/** Starty lokalnych NPC-piesków (wander) — po dzielnicach (świat 3200). */
export const TOWN_NPC_SPAWNS = [
  { id: 'town-npc-1', x: 480, y: 520, color: 0xf5d0c5 },
  { id: 'town-npc-2', x: 1600, y: 600, color: 0xd4c4a8 },
  { id: 'town-npc-3', x: 2600, y: 560, color: 0xc9b1ff },
] as const;

/**
 * Spawny kotków ulicznych — bounds świata, 3 dzielnice, pas chodzenia (W9-A).
 * Konsumowane przez StreetCats (re-export) i minimapę.
 */
export const STREET_CAT_SPAWNS: ReadonlyArray<{ x: number; y: number }> = [
  { x: 420, y: 520 }, // osiedle
  { x: 860, y: 560 }, // osiedle
  { x: 1480, y: 620 }, // rynek
  { x: 1880, y: 545 }, // rynek
  { x: 2480, y: 530 }, // pole
];

/** Dzielnica dla pozycji X (testy / minimapa / E2E). */
export function districtAtX(x: number): TownDistrictId {
  for (const d of TOWN_DISTRICTS) {
    if (x < d.x1) return d.id;
  }
  return TOWN_DISTRICTS[TOWN_DISTRICTS.length - 1]!.id;
}

/** Sceny, do których mapuje R34 — używane w testach kontraktu nawigacji. */
export function townNavigationSceneKeys(): string[] {
  return TOWN_BUILDING_HOTSPOTS.map((h) => h.sceneKey);
}
